import type { SafetyReportDetail, SafetyReportEntry } from "@wallyo/contracts/console/safety";
import { customerFeatureName, t } from "../../i18n";
import { formatDateTime } from "../../i18n/format";
import { casesHref, ALL_CASES } from "../cases/case-route";
import { customerHref } from "../customers/customer-route";
import type { ActionOffer, ReportActionKind } from "./report-actions";
import {
  SEVERITY_TONE,
  STATUS_TONE,
  inForce,
  restrictionTerm,
  reviewOverdue,
  severityName,
  statusName,
  verdictName,
} from "./report-format";
import { reportsHref, type ReportFilters } from "./report-route";

export type ReportDetailViewProps = {
  detail: SafetyReportDetail;
  filters: ReportFilters;
  actions: readonly ActionOffer[];
  now: Date;
  onAction: (kind: ReportActionKind) => void;
};

const authorName = (entry: SafetyReportEntry) =>
  entry.author.type === "system" ? t("reports.entry.system") : entry.author.name;

/** One report: who, what, the restrictions and their review deadlines, then the timeline and the actions. */
export function ReportDetailView(props: ReportDetailViewProps) {
  const { detail: r, filters, now } = props;
  return (
    <div className="card verif-detail report-detail" data-report={r.safetyReportId}>
      <div className="verif-head">
        <div>
          <h3>
            {r.reference} · {t(`reports.category.${r.category}` as const)}
          </h3>
          <p className="meta">{[t(`reports.source.${r.source}` as const), formatDateTime(r.createdAt)].join(" · ")}</p>
        </div>
        <span className="verif-pills">
          <span className={`pill ${SEVERITY_TONE[r.severity]}`}>{severityName(r.severity)}</span>
          <span className={`pill ${STATUS_TONE[r.status]}`}>{statusName(r.status)}</span>
          {r.underLegalHold ? <span className="pill info">{t("reports.pill.legalHold")}</span> : null}
          <span className="meta mono" title={t("reports.detail.versionTitle")}>
            v{r.version}
          </span>
        </span>
      </div>

      {r.needsPoliceReferral ? (
        <div className="verif-banned" role="alert">
          <b>{t("reports.detail.critical")}</b>
          <span>{t("reports.detail.needsPolice")}</span>
        </div>
      ) : null}

      <ul className="kv">
        <li>
          <span>{t("reports.detail.subject")}</span>
          <span>
            <a className="go-link" href={customerHref(r.subject.userId)}>
              {r.subject.name}
            </a>
            {r.reportsAboutSubject > 1 ? (
              <span className="meta"> · {t("reports.list.aboutSubject", { count: r.reportsAboutSubject })}</span>
            ) : null}
          </span>
        </li>
        <li>
          <span>{t("reports.detail.reporter")}</span>
          <span>
            {r.reporter ? (
              <a className="go-link" href={customerHref(r.reporter.userId)}>
                {r.reporter.name}
              </a>
            ) : r.openedBy ? (
              t("reports.detail.openedBy", { name: r.openedBy.name })
            ) : (
              t("reports.detail.noReporter")
            )}
          </span>
        </li>
        <li>
          <span>{t("reports.detail.job")}</span>
          <span className={r.jobId ? "mono" : undefined}>{r.jobId ?? t("reports.detail.none")}</span>
        </li>
        <li>
          <span>{t("reports.detail.case")}</span>
          <span>
            {r.supportCaseId ? (
              <a className="go-link" href={casesHref(ALL_CASES, r.supportCaseId)} data-case="">
                {t("reports.detail.openCase")}
              </a>
            ) : (
              t("reports.detail.none")
            )}
          </span>
        </li>
        <li>
          <span>{t("reports.detail.subjectCase")}</span>
          <span>
            {r.subjectSupportCaseId ? (
              <a className="go-link" href={casesHref(ALL_CASES, r.subjectSupportCaseId)} data-subject-case="">
                {t("reports.detail.openCase")}
              </a>
            ) : (
              t("reports.detail.noSubjectCase")
            )}
          </span>
        </li>
      </ul>

      <h2 className="verif-h">{t("reports.detail.description")}</h2>
      <p className="note">{r.description}</p>

      <Restrictions {...props} />

      {r.verdict ? (
        <>
          <h2 className="verif-h">{t("reports.detail.verdict")}</h2>
          <ul className="kv">
            <li>
              <span>{verdictName(r.verdict)}</span>
              <span className="meta">
                {r.decidedBy && r.decidedAt
                  ? t("reports.detail.decidedBy", { name: r.decidedBy.name, date: formatDateTime(r.decidedAt) })
                  : ""}
              </span>
            </li>
          </ul>
          {r.verdictReason ? <p className="note">{r.verdictReason}</p> : null}
        </>
      ) : null}

      {r.appeal ? (
        <>
          <h2 className="verif-h">{t("reports.detail.appeal")}</h2>
          <p className="meta">{t("reports.detail.appealedAt", { date: formatDateTime(r.appeal.appealedAt) })}</p>
          <p className="note">{r.appeal.message}</p>
          {r.appeal.decision ? (
            <p className="meta" data-appeal-decided="">
              {t(`reports.appeal.${r.appeal.decision}` as const)}
              {r.appeal.decidedBy ? ` · ${r.appeal.decidedBy.name}` : ""}
              {r.appeal.decisionReason ? ` · ${r.appeal.decisionReason}` : ""}
            </p>
          ) : (
            <span className="pill warn">{t("reports.detail.appealPending")}</span>
          )}
        </>
      ) : null}

      {r.policeReferrals.length > 0 ? (
        <>
          <h2 className="verif-h">{t("reports.detail.police")}</h2>
          <ul className="kv">
            {r.policeReferrals.map((referral) => (
              <li key={referral.policeReferralId}>
                <span>
                  {referral.station} · {referral.officerName}
                  {referral.policeReference ? <span className="mono"> · {referral.policeReference}</span> : null}
                  {referral.dataShares.map((share) => (
                    <span className="meta" key={share.policeDataShareId}>
                      <br />
                      {t("reports.detail.shared", {
                        categories: share.categories
                          .map((category) => t(`reports.police.category.${category}` as const))
                          .join(", "),
                        name: share.sharedBy.name,
                        date: formatDateTime(share.sharedAt),
                      })}
                    </span>
                  ))}
                </span>
                <span className="meta">
                  {referral.referredBy.name} · {formatDateTime(referral.referredAt)}
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {r.legalHolds.length > 0 ? (
        <>
          <h2 className="verif-h">{t("reports.detail.holds")}</h2>
          <ul className="kv">
            {r.legalHolds.map((hold) => (
              <li key={hold.legalHoldId}>
                <span>
                  {t(`reports.hold.entity.${hold.entityType}` as const)} · {hold.reason}
                </span>
                <span className="meta">
                  {hold.releasedAt
                    ? t("reports.detail.holdReleased", {
                        date: formatDateTime(hold.releasedAt),
                        name: hold.releasedBy?.name ?? "—",
                      })
                    : t("reports.detail.holdPlaced", {
                        date: formatDateTime(hold.placedAt),
                        name: hold.placedBy.name,
                      })}
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {r.relatedReports.length > 0 ? (
        <>
          <h2 className="verif-h">{t("reports.detail.related")}</h2>
          <ul className="kv" data-related="">
            {r.relatedReports.map((related) => (
              <li key={related.safetyReportId}>
                <a className="go-link" href={reportsHref(filters, related.safetyReportId)}>
                  {related.reference} · {t(`reports.category.${related.category}` as const)}
                </a>
                <span className="verif-pills">
                  <span className={`pill ${SEVERITY_TONE[related.severity]}`}>{severityName(related.severity)}</span>
                  <span className={`pill ${STATUS_TONE[related.status]}`}>{statusName(related.status)}</span>
                  {related.verdict ? <span className="pill mute">{verdictName(related.verdict)}</span> : null}
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      <h2 className="verif-h">{t("reports.detail.timeline")}</h2>
      <div className="timeline case-timeline">
        {r.entries.map((entry) => (
          <div className={`ev${entry.author.type === "staff" ? " staff" : ""}`} key={entry.safetyReportEntryId}>
            <div className="when">
              {formatDateTime(entry.createdAt)} · {t(`reports.entry.${entry.type}` as const)} · {authorName(entry)}
            </div>
            {entry.body ? <div className="note">{entry.body}</div> : null}
            {entry.evidenceUrl ? (
              <a className="go-link" href={entry.evidenceUrl} target="_blank" rel="noreferrer noopener">
                {t("reports.entry.evidenceLink")}
              </a>
            ) : null}
          </div>
        ))}
      </div>

      <Actions {...props} now={now} />
    </div>
  );
}

function Restrictions({ detail: r, now }: ReportDetailViewProps) {
  if (r.restrictions.length === 0) return null;
  return (
    <>
      <h2 className="verif-h">{t("reports.detail.restrictions")}</h2>
      <p className="foot">{t("reports.detail.restrictionsHint")}</p>
      <ul className="kv">
        {r.restrictions.map((restriction) => {
          const live = inForce(restriction, now);
          const overdue = reviewOverdue(restriction, now);
          return (
            <li key={restriction.safetyRestrictionId} data-restriction={restriction.safetyRestrictionId}>
              <span>
                <b>{t(`reports.restriction.${restriction.kind}` as const)}</b>
                <br />
                <span className="meta">{restrictionTerm(restriction)}</span>
                {restriction.switchedOff.length > 0 ? (
                  <span className="meta">
                    <br />
                    {t("reports.restriction.switchedOff", {
                      features: restriction.switchedOff.map(customerFeatureName).join(", "),
                    })}
                  </span>
                ) : null}
                {restriction.liftedAt ? (
                  <span className="meta">
                    <br />
                    {t("reports.restriction.lifted", {
                      date: formatDateTime(restriction.liftedAt),
                      name: restriction.liftedBy?.name ?? "—",
                    })}
                    {restriction.liftReason ? ` · ${restriction.liftReason}` : ""}
                  </span>
                ) : null}
              </span>
              <span className="verif-pills">
                {overdue ? (
                  <span className="pill bad" data-overdue="">
                    {t("reports.pill.reviewOverdue")}
                  </span>
                ) : null}
                <span className={`pill ${live ? "bad" : "mute"}`}>
                  {live ? t("reports.restriction.inForce") : t("reports.restriction.ended")}
                </span>
              </span>
            </li>
          );
        })}
      </ul>
    </>
  );
}

const BLOCK_TEXT = {
  state: null,
  feature: null,
  ownVerdict: "reports.block.ownVerdict",
  ownerOnly: "reports.block.ownerOnly",
  noReferral: "reports.block.noReferral",
} as const;

function Actions({ actions, onAction }: ReportDetailViewProps) {
  const offered = actions.filter((action) => action.block !== "state" && action.block !== "feature");
  const explained = offered.filter((action) => action.block !== null);
  return (
    <div className="case-actions report-actions">
      <h2 className="verif-h">{t("reports.detail.actions")}</h2>
      {offered.length === 0 ? <p className="foot">{t("reports.detail.noActions")}</p> : null}
      <div className="btns">
        {offered.map((action) => (
          <button
            key={action.kind}
            type="button"
            className={`btn${action.kind === "ban" ? " danger" : ""}`}
            disabled={action.block !== null}
            data-action={action.kind}
            onClick={() => onAction(action.kind)}
          >
            {t(`reports.action.${action.kind}` as const)}
          </button>
        ))}
      </div>
      {explained.map((action) => {
        const text = action.block ? BLOCK_TEXT[action.block] : null;
        return text ? (
          <p className="foot" key={action.kind}>
            {t(text)}
          </p>
        ) : null;
      })}
    </div>
  );
}
